import useUrlState from '@ahooksjs/use-url-state';
import Icon, { ExpandOutlined, HistoryOutlined, LoadingOutlined, ShrinkOutlined } from '@ant-design/icons';
import { CopilotKit, useCopilotChatInternal } from '@copilotkit/react-core';
import {
  CopilotChat,
  UserMessage as DefaultUserMessage,
  ImageRenderer as DefaultImageRenderer,
  RenderMessageProps,
} from '@copilotkit/react-ui';
import { useAsyncEffect, useLocalStorageState } from 'ahooks';
import { Tooltip, Popover, List, Empty, Spin, Space } from 'antd';
import classNames from 'classnames';
import { isEmpty, isString } from 'lodash';
import React, { Fragment, useState, useCallback, useMemo, useEffect, createContext, useContext, useRef } from 'react';
import * as uuid from 'uuid';
import BuglyCopilotIcon from '@/assets/images/header/bugly-copilot-icon.svg?react';
import Icon1 from '@/assets/images/logo-selector/icons/icon1.svg?react';
import Icon2 from '@/assets/images/logo-selector/icons/icon2.svg?react';
import Icon3 from '@/assets/images/logo-selector/icons/icon3.svg?react';
import Icon4 from '@/assets/images/logo-selector/icons/icon4.svg?react';
import Icon5 from '@/assets/images/logo-selector/icons/icon5.svg?react';
import Icon6 from '@/assets/images/logo-selector/icons/icon6.svg?react';
import Icon7 from '@/assets/images/logo-selector/icons/icon7.svg?react';
import Icon8 from '@/assets/images/logo-selector/icons/icon8.svg?react';
import { getUserId } from '@/infrastructure/auth';
import { ENV } from '@/infrastructure/env-config';
import { getProductId } from '@/infrastructure/product-info';
import { useStores } from '@/presentation/store';
import { aiAgentServer } from '@/service/agent';
import { aiCopilotServer } from '@/service/ai-copilot';
import { agentService } from './agent-service';
import { CustomAssistantMessage } from './assistant-message';
import { CopilotInput } from './copilot-input';
import DrawerClose from './icons/drawer-close.react.svg?react';
import DragImgIcon from './icons/empty-img-simple.svg?react';
import CreateChatIcon from './icons/new-chat.react.svg?react';
// 导入所有 logo 图标

import '@copilotkit/react-ui/styles.css';
import style from './index.module.scss';
import { CustomCopilotMessages } from './messages';
import { ToolMessage } from './tool';
import { CustomUserMessage } from './user-message';

// Logo 图标配置
const LOGO_CONFIG: Record<string, React.ComponentType> = {
  icon1: Icon1,
  icon2: Icon2,
  icon3: Icon3,
  icon4: Icon4,
  icon5: Icon5,
  icon6: Icon6,
  icon7: Icon7,
  icon8: Icon8,
};

// 获取 agent logo 图标
const getAgentLogo = (logo?: string): React.ReactNode => {
  const logoKey = logo || 'icon1'; // 如果没有logo，默认使用icon1
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const LogoComponent = LOGO_CONFIG[logoKey] || LOGO_CONFIG.icon1; // 如果找不到对应logo，使用默认icon1

  return <LogoComponent />;
};

// 会话类型定义
interface ChatSession {
  id: string;
  title: string;
  threadId: string;
  createdAt: number;
  updatedAt: number;
  preview?: string; // 会话预览文本
}

// 扩展的文件接口，包含上传状态
export interface ExtendedFile {
  id: string; // UUID
  file: File; // 原始文件
  uploadStatus: 'pending' | 'uploading' | 'success' | 'error';
  uploadProgress?: number; // 上传进度 0-100
  uploadUrl?: string; // 上传成功后的URL
  errorMessage?: string; // 错误信息
}

interface AgentOption {
  label: string;
  value: string;
  icon: React.ReactNode;
  description: string;
  bgColor: string;
  runtimeUrl: string; // agent 对应的 runtime URL
  id?: number; // 添加 API 返回的 id
  createType?: number; // 添加 createType 字段，用于判断是否为官方 agent (1为官方)
  logo?: string; // agent logo 标识，如 'icon1', 'icon2' 等
}

// 内嵌模式配置
export interface InlineConfig {
  width?: number;
  agentId?: number;
  /** 传递给后端的上下文数据，会被合并到 CopilotKit properties 中 */
  context?: Record<string, unknown>;
  /** inline 模式下的输入框占位提示 */
  placeholder?: string;
  /** inline 模式下的空状态建议问题列表，不传则使用默认 */
  suggestions?: string[];
  /** inline 模式下的空状态描述文案（支持 JSX，如含 <br /> 换行），不传则使用默认 */
  emptyDescription?: React.ReactNode;
}

// 创建 Agent Context
interface AgentContextValue {
  currentAgent: string;
  setCurrentAgent: (agent: string, callback?: () => void) => void;
  isWaiting: boolean;
  setIsWaiting: (waiting: boolean) => void;
  agentOptions: AgentOption[]; // 添加 agentOptions 到 context
  quickText: string;
  setQuickText: (text: string) => void;
  files: ExtendedFile[];
  setFiles: React.Dispatch<React.SetStateAction<ExtendedFile[]>>;
  addFiles: (files: File[]) => void; // 添加文件并上传
  // AgentTools 相关
  agents: string[];
  setAgents: (agents: string[]) => void;
  handleRemoveAgent: (agentValue: string) => void;
  handleOpenAgentDrawer: () => void;
  threadId: string;
  /** 空状态建议问题列表 */
  suggestions?: string[];
  /** 空状态描述文案（支持 JSX，如含 <br /> 换行） */
  emptyDescription?: React.ReactNode;
}

const AgentContext = createContext<AgentContextValue | undefined>(undefined);

// 自定义 Hook 用于访问 Agent Context
export const useAgentContext = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgentContext must be used within AgentProvider');
  }
  return context;
};

export interface CopilotAgentWrapperRef {
  /** 触发 inline 输入框自动发送一条消息（复用 quickText 机制，会经过 onBeforeSend 自动追加 context） */
  sendQuickText: (text: string) => void;
}

export const CopilotAgentWrapper = ({ inline, actionRef }: {
  inline?: InlineConfig;
  actionRef?: React.MutableRefObject<CopilotAgentWrapperRef | null>;
}) => {
  const isInline = !!inline;
  const [model, setModel] = useLocalStorageState('bugly-copilot-model-v2', {
    defaultValue: 'qwen3-235b-a22b-2507-fp8',
  });

  const productId = getProductId();
  const [threadId, setThreadId] = useState(uuid.v4());
  const [currentAgent, setCurrentAgentState] = useState(inline?.agentId ? String(inline.agentId) : ''); // 当前激活的 agent
  const [isWaiting, setIsWaiting] = useState(false);
  const [agentOptions, setAgentOptions] = useState<AgentOption[]>([]);
  const [quickText, setQuickText] = useState('');
  const [files, setFiles] = useState<ExtendedFile[]>([]);

  // inline 模式：agents 固定为传入的 agentId，不读写 localStorage，与浮动球实例隔离
  const inlineAgents = useMemo(
    () => (inline?.agentId ? [String(inline.agentId)] : []),
    [inline?.agentId],
  );
  const [storedAgents, setStoredAgents] = useLocalStorageState<string[]>('bugly-copilot-agents-v1', {
    defaultValue: [],
  });
  const agents = isInline ? inlineAgents : storedAgents;
  const noopSetAgents = useCallback(() => {}, []);
  const setAgents = isInline ? noopSetAgents : setStoredAgents;

  // 使用 ref 来存储打开抽屉的函数
  const openAgentDrawerRef = useRef<(() => void) | null>(null);

  // 添加文件（上传逻辑在FileItem组件中处理）- 使用 useCallback 稳定引用
  const addFiles = useCallback((newFiles: File[]) => {
    const extendedFiles: ExtendedFile[] = newFiles.map(file => ({
      id: uuid.v4(),
      file,
      uploadStatus: 'pending',
    }));

    // 添加到文件列表前面
    setFiles(prevFiles => [...extendedFiles, ...prevFiles]);
  }, [setFiles]);

  // 加载 agent 列表
  useAsyncEffect(async () => {
    try {
      const response = await aiAgentServer.getAgentsByUser({});

      if (response?.baseRsp?.code === 0 && response?.agentInfos) {
        const mappedAgents: AgentOption[] = response.agentInfos
          .filter((agent: any) => {
            if (agent.visibleControl === 2) {
              return !isEmpty(agent.productId) && agent.productId.includes(productId);
            }

            return true;
          }) // 只显示可见的 agent
          .map((agent: any) => ({
            id: agent.id,
            label: agent.agentName || '',
            value: `${agent.id}` || '',
            icon: getAgentLogo(agent.logo), // 使用动态 logo 获取函数
            description: agent.agentDesc || '',
            bgColor: '#E8F4FF', // 默认背景色，可以根据需要扩展
            runtimeUrl: '/copilot/agent', // 默认 runtime URL，可以根据需要扩展
            createType: agent.createType, // 保存 createType 用于判断是否为官方 agent
            logo: agent.logo, // 保存原始 logo 字段
          }));

        setAgentOptions(mappedAgents);
      }
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  }, []);

  const onReset = useCallback(() => {
    const newThreadId = uuid.v4();
    setThreadId(newThreadId);
    setCurrentAgentState('');
  }, []);

  // 暴露 actionRef：外部可通过 sendQuickText 自动触发 inline 输入框发送消息
  useEffect(() => {
    if (actionRef) {
      // eslint-disable-next-line no-param-reassign
      actionRef.current = {
        sendQuickText: (text: string) => setQuickText(text),
      };
    }
  }, [actionRef, setQuickText]);

  // 重置会话但不改变当前 agent
  const onResetSessionOnly = useCallback(() => {
    const newThreadId = uuid.v4();
    setThreadId(newThreadId);
  }, []);

  const changeAgent = useCallback((agentName: string, callback?: () => void) => {
    setCurrentAgentState(agentName);

    setTimeout(() => {
      callback?.();
    });
  }, []);

  // 确保 agents 总是有值，并且与 agentOptions 同步
  const safeAgents = useMemo(() => {
    // inline 模式：agents 已固定，直接返回
    if (isInline) return agents;

    if (!agentOptions.length) return [];

    // 如果 agents 为空或者包含不存在的 agent，使用所有官方 agents (createType === 1)
    if (!agents || agents.length === 0
      || !agents.some((agent: string) => agentOptions.find((opt: AgentOption) => opt.value === agent))) {
      // 获取所有官方 agents
      const officialAgents = agentOptions
        .filter((opt: AgentOption) => opt.createType === 1)
        .map((opt: AgentOption) => opt.value);

      if (officialAgents.length > 0) {
        return officialAgents;
      }
      // 如果没有官方 agents，使用第一个可用的 agent
      const firstAgent = agentOptions[0]?.value;
      return firstAgent ? [firstAgent] : [];
    }

    // 过滤掉不存在的 agents
    return agents
      ?.filter((agent: string) => agentOptions.find((opt: AgentOption) => opt.value === agent)) || [];
  }, [agents, agentOptions, isInline]);

  // 移除 agent - 使用 useCallback 稳定引用
  const handleRemoveAgent = useCallback((agentValue: string) => {
    setAgents((prevAgents) => {
      const safeAgentsList = (prevAgents || []).filter(Boolean);
      return safeAgentsList.filter((a: any) => a !== agentValue);
    });
  }, [setAgents]);

  // 打开抽屉的包装函数 - 使用 useCallback 稳定引用
  const handleOpenAgentDrawer = useCallback(() => {
    if (openAgentDrawerRef.current) {
      openAgentDrawerRef.current();
    }
  }, []);

  // 提供 Context 值 - 使用 useMemo 稳定对象引用
  const contextValue = useMemo(() => ({
    currentAgent,
    setCurrentAgent: changeAgent,
    isWaiting,
    setIsWaiting,
    agentOptions,
    quickText,
    setQuickText,
    files,
    setFiles,
    addFiles,
    // AgentTools 相关
    agents: safeAgents,
    setAgents,
    handleRemoveAgent,
    handleOpenAgentDrawer,
    threadId,
    suggestions: inline?.suggestions,
    emptyDescription: inline?.emptyDescription,
  }), [
    currentAgent,
    changeAgent,
    isWaiting,
    setIsWaiting,
    agentOptions,
    quickText,
    setQuickText,
    files,
    setFiles,
    addFiles,
    safeAgents,
    setAgents,
    handleRemoveAgent,
    handleOpenAgentDrawer,
    threadId,
    inline?.suggestions,
    inline?.emptyDescription,
  ]);

  return (
    <AgentContext.Provider value={contextValue}>
      <CopilotKit
        properties={{
          ...inline?.context,
          modelName: model,
          threadMetadata: {
            account_id: getUserId(),
          },
          agent_id: `${currentAgent}`,
          base_url: location.origin,
        }}
        runtimeUrl={'/copilot/agent'} // 使用动态的 runtimeUrl
        agent={'dynamic_agent'} // 动态 agent
        headers={{
          'X-ProductId': getProductId(),
        }}
        threadId={threadId}
      >
        <CopilotAgent
          model={model}
          setModel={setModel}
          threadId={threadId}
          setThreadId={setThreadId}
          onReset={onReset}
          onResetSessionOnly={onResetSessionOnly}
          agentOptions={agentOptions} // 传递动态的 agentOptions
          openAgentDrawerRef={openAgentDrawerRef} // 传递 ref
          inline={inline}
        />
      </CopilotKit>
    </AgentContext.Provider>
  );
};

enum DrawerSize {
  Large = 800,
  Medium = 480,
}

export const CopilotAgent = (props: any) => {
  const { setThreadId, onResetSessionOnly, openAgentDrawerRef, inline } = props;
  const inlineConfig = inline as InlineConfig | undefined;
  const isInline = !!inlineConfig;
  const {
    currentAgent,
    setCurrentAgent,
    addFiles,
    agents,
    agentOptions,
    setAgents,
  } = useAgentContext(); // 从 Context 中获取
  const [sessionListVisible, setSessionListVisible] = useState(false);
  const [agentDrawerVisible, setAgentDrawerVisible] = useState(false);
  const [agentDrawerClosing, setAgentDrawerClosing] = useState(false);
  const [tempSelectedAgents, setTempSelectedAgents] = useState<string[]>([]);

  // 全局 @ 下拉面板状态
  const [globalAgentDropdown, setGlobalAgentDropdown] = useState({
    visible: false,
    position: { top: 0, left: 0, width: 0 },
    highlightedIndex: 0,
    availableOptions: [] as any[],
    onSelect: null as ((value: string, label: string) => void) | null,
  });
  const globalDropdownRef = useRef<HTMLDivElement>(null);

  // 打开 agent 抽屉的函数
  const handleOpenAgentDrawer = useCallback(() => {
    setTempSelectedAgents([...agents]);
    setAgentDrawerVisible(true);
  }, [agents]);

  // 将打开抽屉的函数赋值给 ref
  useEffect(() => {
    if (openAgentDrawerRef) {
      openAgentDrawerRef.current = handleOpenAgentDrawer;
    }
  }, [openAgentDrawerRef, handleOpenAgentDrawer]);

  // 键盘导航时滚动到可见区域
  useEffect(() => {
    if (globalAgentDropdown.visible && globalDropdownRef.current && globalAgentDropdown.highlightedIndex >= 0) {
      const dropdown = globalDropdownRef.current;
      const highlightedItem = dropdown.children[globalAgentDropdown.highlightedIndex] as HTMLElement;

      if (highlightedItem) {
        const dropdownRect = dropdown.getBoundingClientRect();
        const itemRect = highlightedItem.getBoundingClientRect();

        // 检查是否需要滚动
        if (itemRect.bottom > dropdownRect.bottom) {
          // 项目在底部超出视图，向下滚动
          highlightedItem.scrollIntoView({ block: 'end', behavior: 'smooth' });
        } else if (itemRect.top < dropdownRect.top) {
          // 项目在顶部超出视图，向上滚动
          highlightedItem.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }
    }
  }, [globalAgentDropdown.highlightedIndex, globalAgentDropdown.visible]);

  const layoutStore = useStores().LayoutStore;
  const copilotChatVisible = isInline || layoutStore.copilotChatVisible;
  const [state] = useUrlState();
  const { reset, sendMessage, setMessages, runChatCompletion } = useCopilotChatInternal();
  const { setIsWaiting } = useAgentContext();

  // 拖拽上传相关状态
  const [isDragOver, setIsDragOver] = useState(false);
  const [, setDragCounter] = useState(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const { setCopilotVisible, setCopilotChatWidth } = layoutStore;
  const copilotChatWidth = isInline ? (inlineConfig?.width || DrawerSize.Medium) : layoutStore.copilotChatWidth;

  // 会话列表状态管理
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [sessionLoading, setSessionLoading] = useState(false);

  // 标记是否已经在首条用户消息后追加了上下文（仅首条消息）
  const contextAppended = useRef(false);

  const onBeforeSend = async (text: string, extendedFiles: ExtendedFile[]) => {
    // inline 模式下，在首条用户消息后追加 context（通过 inlineConfig.context 传入 contextId 标记）
    let messageText = text;
    if (isInline && inlineConfig?.context && !contextAppended.current) {
      contextAppended.current = true;
      const { contextId, ...rest } = inlineConfig.context;
      const marker = typeof contextId === 'string' && contextId ? contextId : '';
      const lines = Object.entries(rest)
        .filter(([, v]) => v !== null && v !== '' && v !== undefined)
        .map(([k, v]) => `${k}: ${v}`);
      if (marker && lines.length) {
        messageText = `${text}\n${marker}\n${lines.join('\n')}`;
      }
    }

    const textMessage = {
      id: uuid.v4(),
      role: 'user',
      content: messageText,
    };

    if (!isEmpty(extendedFiles)) {
      // 构建包含完整文件信息的消息
      const fileMessage = {
        type: 'file',
        files: extendedFiles.map(ef => ({
          id: ef.id,
          file: ef.file,
          uploadStatus: ef.uploadStatus,
          uploadProgress: ef.uploadProgress,
          uploadUrl: ef.uploadUrl,
          errorMessage: ef.errorMessage,
          fileSize: ef.file.size, // 保存文件大小
          fileName: ef.file.name, // 保存文件名
        })),
        textMessage,
      };

      await sendMessage({
        id: uuid.v4(),
        role: 'user',
        content: JSON.stringify(fileMessage),
      }, {
        followUp: false,
      });
    } else {
      await sendMessage({
        id: uuid.v4(),
        role: 'user',
        content: messageText,
      }, {
        followUp: false,
      });
    }

    setIsWaiting(true);
  };

  // 创建包装的 Input 组件，传递 agent 相关参数
  const WrappedCopilotInput = useCallback((inputProps: any) => (
    <CopilotInput
      {...inputProps}
      availableAgents={agents}
      currentAgent={currentAgent}
      onAgentChange={(agent: string) => {
        setCurrentAgent(agent, () => {
          runChatCompletion();
        });
      }}
      onBeforeSend={onBeforeSend}
      onResetSession={onResetSessionOnly}
      onAddFiles={addFiles}
      placeholder={inlineConfig?.placeholder}
      isInline={isInline}
      // 传递全局下拉面板控制函数
      globalDropdownControl={{
        showDropdown: (position: any, options: any[], onSelect: any) => {
          setGlobalAgentDropdown({
            visible: true,
            position,
            highlightedIndex: 0,
            availableOptions: options,
            onSelect,
          });
        },
        hideDropdown: () => {
          setGlobalAgentDropdown(prev => ({ ...prev, visible: false }));
        },
        updateHighlight: (index: number) => {
          setGlobalAgentDropdown(prev => ({ ...prev, highlightedIndex: index }));
        },
        getDropdownRef: () => globalDropdownRef.current,
      }}
    />
  ), [agents, currentAgent, setCurrentAgent, onResetSessionOnly, addFiles, onBeforeSend, isInline, inlineConfig]);

  // renderMessage 包裹层：inline 模式下按 contextId 截断用户消息显示
  const renderMessage = useCallback((props: RenderMessageProps) => {
    const { message } = props;
    const contextId = isInline && typeof inlineConfig?.context?.contextId === 'string'
      ? (inlineConfig.context.contextId as string) : '';

    // 仅 inline 模式 + 有 contextId 时截断用户消息内容
    if (message.role === 'user' && contextId) {
      let displayContent: any = message.content;
      if (typeof displayContent === 'string') {
        const idx = displayContent.indexOf(`\n${contextId}\n`);
        if (idx !== -1) {
          displayContent = displayContent.substring(0, idx);
        }
      }
      return RenderMessage({ ...props, message: { ...message, content: displayContent } });
    }

    // 其他消息直接透传
    return RenderMessage(props);
  }, [inlineConfig, isInline]);

  const loadSessions = useCallback(async () => {
    try {
      const resp = await aiCopilotServer.getSessionList({ limit: 200, offset: 0 });
      const mapped = (resp.sessions || []).map(s => ({
        id: s.sessionId || uuid.v4(),
        title: s.title || '新对话',
        threadId: s.sessionId || uuid.v4(),
        createdAt: (Number((s as any).createTime) || 0) * 1000,
        updatedAt: (Number((s as any).updateTime || (s as any).createTime) || 0) * 1000,
        preview: s.title || '',
      }));
      setSessions(mapped);
    } finally {

    }
  }, []);

  // 切换宽度
  const toggleWidth = useCallback(() => {
    if (isInline) return;
    setCopilotChatWidth(copilotChatWidth === DrawerSize.Large ? DrawerSize.Medium : DrawerSize.Large);
  }, [copilotChatWidth, isInline]);

  const onReset = () => {
    props.onReset?.();  // 生成新的 threadId
    reset();            // 清空消息列表
    setCurrentSessionId('');  // 清空当前会话 ID，以便创建新会话
  };

  // 拖拽事件处理函数
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);

    // 检查是否包含文件
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => {
      const newCounter = prev - 1;
      if (newCounter === 0) {
        setIsDragOver(false);
      }
      return newCounter;
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragCounter(0);

    const dragFiles = Array.from(e.dataTransfer.files);
    if (dragFiles.length > 0) {
      // 添加文件并开始上传
      addFiles(dragFiles);
    }
  }, [addFiles]);

  const onLoadSession = useCallback((sessionId: string) => {
    const session = sessions?.find(s => s.id === sessionId);
    if (session) {
      setThreadId(session.threadId);
      setCurrentSessionId(sessionId);
    }
  }, [sessions, setThreadId, setCurrentSessionId]);

  // useCopilotAction({
  //   name: 'transfer_to_agent',
  //   render: ({ args, status, result }) => <div>123</div>,
  // });


  useEffect(() => {
    if (copilotChatVisible && !isInline) {
      loadSessions();
    }
  }, [copilotChatVisible, loadSessions, isInline]);

  // 每次打开对话历史面板时都拉取最新数据
  useEffect(() => {
    if (sessionListVisible) {
      loadSessions();
    }
  }, [sessionListVisible, loadSessions]);

  const parseMsg = (msg: any, agentId?: string, agentName?: string, msgId?: string) => {
    if (msg.type === 'tool_call') {
      const originalMsgs = [
        {
          toolCalls: [
            {
              function: {
                name: msg.name,
                arguments: msg.arguments,
              },
              id: msg.id,
              type: 'function',
            },
          ],
          role: 'assistant',
          id: msg.id,
          name: msg.name,
          agentId,
          agentName,
          msgId,
        },
        {
          toolName: msg.name,
          toolCallId: msg.id,
          content: msg.result,
          role: 'tool',
          id: msg.id,
          agentId,
          agentName,
          msgId,
        },
      ];

      return originalMsgs;
    }

    return [{
      ...msg,
      role: msg.role === 0 ? 'user' : 'assistant',
      agentId,
      agentName,
      id: msg.id || msgId,
    }];
  };

  const handleLoadSession = async (sessionId: string) => {
    onLoadSession(sessionId);
    setSessionListVisible(false);
    setSessionLoading(true);
    setCurrentAgent('');
    const sessionHistory = await aiCopilotServer.getHistoryQA({
      sessionId,
    });
    try {
      const historyMsgs: any = [];
      sessionHistory?.msg?.forEach((item) => {
        try {
          const msgs = JSON.parse(item?.content ?? '');
          if (isString(msgs)) {
            historyMsgs.push({
              ...item,
              role: item.role === 0 ? 'user' : 'assistant',
              content: item.content,
            });
          } else {
            msgs.forEach((msg: any) => {
              historyMsgs.push(...parseMsg(msg, `${item.agentId ?? ''}`, item.agentName ?? '', item.msgId ?? ''));
            });
          }
        } catch (e) {
          historyMsgs.push({
            ...item,
            role: item.role === 0 ? 'user' : 'assistant',
            content: item.content,
          });
        }
      });

      reset();
      agentService.initMessages(historyMsgs);
      setMessages(historyMsgs as any);
    } catch (e) {
      console.error(e);
    } finally {
      setSessionLoading(false);
    }
  };

  // 处理临时选择 agent - 点击即选中
  const handleToggleTempAgent = (agentValue: string) => {
    if (tempSelectedAgents.includes(agentValue)) {
      // 取消选择
      const newTempSelected = tempSelectedAgents.filter(a => a !== agentValue);
      setTempSelectedAgents(newTempSelected);
      setAgents(newTempSelected); // 立即应用到实际状态
    } else {
      // 添加选择
      const newTempSelected = [...tempSelectedAgents, agentValue];
      setTempSelectedAgents(newTempSelected);
      setAgents(newTempSelected); // 立即应用到实际状态
    }
  };

  // 关闭 agent 抽屉（不保存）
  const handleCloseAgentDrawer = () => {
    setAgentDrawerClosing(true);
    setTimeout(() => {
      setAgentDrawerVisible(false);
      setAgentDrawerClosing(false);
      setTempSelectedAgents([]);
    }, 300); // 动画持续时间
  };

  // 确认选择
  const handleConfirmAgentSelection = () => {
    setAgents([...tempSelectedAgents]);
    handleCloseAgentDrawer();
  };

  const onCreateAgent = () => {
    // 根据环境选择跳转域名
    const targetDomain = ENV.isProd ? 'buglyagent.woa.com' : 't.buglyagent.woa.com';
    const targetUrl = `https://${targetDomain}`;

    // 新窗口打开
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  // 会话列表内容
  const sessionListContent = (
    <div className={style.sessionListContainer}>
      <div className={style.sessionListHeader}>
        <span>对话历史</span>
      </div>
      {!sessions || sessions.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无对话历史"
          className={style.sessionListEmpty}
        />
      ) : (
        <List
          className={style.sessionList}
          dataSource={sessions}
          renderItem={(session: ChatSession) => (
            <List.Item
              className={classNames(style.sessionListItem, {
                [style.sessionListItemActive]: session.id === currentSessionId,
              })}
              onClick={() => handleLoadSession(session.id)}
            >
              <div className={style.sessionListItemContent}>
                <div className={style.sessionListItemTitle}>{session.title}</div>
              </div>
            </List.Item>
          )}
        />
      )}
    </div>
  );

  const onClose = () => {
    if (isInline) return;
    setCopilotVisible(false);
  };

  if (!state.productId) {
    return null;
  }

  return (
    <Fragment>
      {copilotChatVisible && (
        <div
          className={classNames(style.drawer, { [style.inlineDrawer]: isInline })}
          style={{ width: isInline ? '100%' : copilotChatWidth }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
        <div className={style.header}>
          <div className={style.drawerTitle}>
            <div className={style.drawerTitleLeft}>
              <BuglyCopilotIcon />
              {isInline ? (
                <div className={style.inlineTitle}>
                  <span>AI智能分析</span>
                  <div className={style.inlineSubtitle}>智能问答，点击预制标签或提问，快速锁定值得关注的内容</div>
                </div>
              ) : (
                <span>Bugly Copilot</span>
              )}
            </div>
            {!isInline && <div className={style.drawerTitleRight}>
            </div>}
          </div>

          <div className={style.drawerExtra}>
            {!isInline && (
              <>
                <Popover
                  content={sessionListContent}
                  trigger="click"
                  placement="bottomRight"
                  visible={sessionListVisible}
                  onVisibleChange={setSessionListVisible}
                  overlayClassName={style.sessionListPopover}
                >
                  <Tooltip title="">
                    <HistoryOutlined />
                  </Tooltip>
                </Popover>
                <Tooltip title="">
                  <CreateChatIcon
                    onClick={onReset}
                  />
                </Tooltip>
                {/* 展开/收起按钮 */}
                <Tooltip title=''>
                  <Icon
                    onClick={toggleWidth}
                    type='primary'
                    component={(copilotChatWidth === DrawerSize.Medium ? ExpandOutlined : ShrinkOutlined) as any}
                  />
                </Tooltip>
                <span
                  onClick={onClose}
                  className={style.drawerCloseIcon}
                >
                  <DrawerClose />
                </span>
              </>
            )}
          </div>
        </div>
        <div className={classNames('flex flex-column', style.drawerContentWrapper)}>
          {/* Loading 效果 */}
          {sessionLoading && (
            <div className={style.sessionLoadingOverlay}>
              <Spin tip="加载会话中..." />
            </div>
          )}

          {/* 全局 @ 下拉面板 - 与 emptyStateOverlay 同级，确保 z-index 正确 */}
          {globalAgentDropdown.visible && (
            <div
              ref={globalDropdownRef}
              className={style.globalAgentDropdown}
              style={{
                position: 'absolute',
                bottom: 165,
                left: 18,
                width: 240,
                zIndex: 1020, // 确保比 emptyStateOverlay 高
              }}
            >
              {globalAgentDropdown.availableOptions.map((agent, index) => (
                <div
                  key={agent.value}
                  className={`${style.globalAgentDropdownItem} ${index === globalAgentDropdown.highlightedIndex ? style.highlighted : ''}`}
                  onClick={() => {
                    if (globalAgentDropdown.onSelect) {
                      globalAgentDropdown.onSelect(agent.value, agent.label);
                    }
                  }}
                  onMouseEnter={() => {
                    setGlobalAgentDropdown(prev => ({ ...prev, highlightedIndex: index }));
                  }}
                >
                  <span className={style.agentIcon}>{agent.icon}</span>
                  <span className={style.agentName}>{agent.label}</span>
                </div>
              ))}
            </div>
          )}

          <div
            ref={chatContainerRef}
            className={style.chatContainer}
          >
            <CopilotChat
              Input={WrappedCopilotInput}
              className={classNames('h-full mx-auto flex-1', style.chatAgent)}
              labels={{
                placeholder: '请描述希望分析的数据',
              }}
              RenderMessage={renderMessage}
              Messages={CustomCopilotMessages}
              onInProgress={(inProgress) => {
                if (inProgress) {
                  setIsWaiting(false);
                }
              }}
            />


          </div>
          {/* AgentTools 已迁移到 copilot-input 中 */}

          {/* Agent 选择抽屉 - 从 copilot 面板内弹出 */}
          {agentDrawerVisible && (
            <div
              className={classNames(style.agentDrawerMask, {
                [style.agentDrawerMaskClosing]: agentDrawerClosing,
              })}
              onClick={handleCloseAgentDrawer}
            >
              <div
                className={classNames(style.agentDrawerContent, {
                  [style.agentDrawerContentClosing]: agentDrawerClosing,
                })}
                onClick={e => e.stopPropagation()}
              >
                <div className={style.agentDrawerHeader}>
                  <span>选择智能体</span>
                  <Space size={8}>
                    <Space size={4} style={{ cursor: 'pointer' }} onClick={onCreateAgent}>
                      <span>+</span>
                      <span style={{ fontWeight: 400, fontSize: 14, alignItems: 'center' }}>创建Agent</span>
                    </Space>
                    <span className={style.agentDrawerClose} onClick={handleCloseAgentDrawer}>×</span>
                  </Space>
                </div>
                <div style={{ flex: 1, overflow: 'auto' }}>
                  <div className={style.agentOptionsContainer}>
                    {agentOptions.map((option: AgentOption) => {
                      const isSelected = tempSelectedAgents.includes(option.value);

                      return (
                        <div
                          key={option.value}
                          className={`${style.agentOptionCard} ${isSelected ? style.agentOptionCardSelected : ''}`}
                          onClick={() => handleToggleTempAgent(option.value)}
                        >
                          <div className={style.agentOptionIcon}>{option.icon}</div>
                          <div className={style.agentOptionContent}>
                            <div className={style.agentOptionLabel}>{option.label}</div>
                            <div className={style.agentOptionDescription}>{option.description}</div>
                          </div>
                          {isSelected && (
                            <div className={style.agentOptionBadge}></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className={style.agentDrawerFooter}>
                  <button className={style.agentDrawerConfirmBtn} onClick={handleConfirmAgentSelection}>
                    确认
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 拖拽上传蒙层 - 覆盖整个面板包括header */}
        {isDragOver && (
          <div className={style.dragOverlay}>
            <div className={style.dragOverlayContent}>
              <div className={style.dragOverlayIcon}><DragImgIcon /></div>
              <div className={style.dragOverlayTitle}>拖拽到此处即可上传</div>
              <div className={style.dragOverlayDescription}>当前支持的文件格式包含txt, zip, gzip</div>
            </div>
          </div>
        )}
      </div>
      )}
    </Fragment>
  );
};

function RenderMessage({
  ImageRenderer = DefaultImageRenderer,
  ...props
}: RenderMessageProps) {
  const {
    message,
    inProgress,
    index,
    isCurrentMessage,
    onRegenerate,
    onCopy,
    onThumbsUp,
    onThumbsDown,
    markdownTagRenderers,
  } = props;

  // 用户消息正常渲染
  if (message.role === 'user') {
    const isLoading = inProgress && isCurrentMessage && !message.content;
    return (
      <CustomUserMessage
        key={index}
        rawData={message}
        data-message-role="user"
        message={message}
        ImageRenderer={ImageRenderer}
        isLoading={isLoading}
        DefaultUserMessage={DefaultUserMessage}
        inProgress={inProgress}
        isCurrentMessage={isCurrentMessage}
      />
    );
  }

  // Assistant 消息：包括带 toolCalls 的消息
  if (message.role === 'assistant') {
    const toolExecutions = (message as any).__mergedToolExecutions || [];

    // 如果是 toolCall 消息（没有 content，但有 toolCalls）- 使用统一的样式
    if (message.toolCalls && !message.content) {
      return (
        <>
          {message.toolCalls.map((call: any) => (
            <div key={call.id}
              className="copilotKitMessage copilotKitToolCallMessage" data-message-role="assistant"
              style={{
                border: '1px solid var(--grey-blue-3, #DCE3EE)',
                borderRadius: 6,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  backgroundColor: '#F7F8FA',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#1e304a',
                  height: '32px',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>工具调用：{call.function.name}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '0 0 6px 6px' }}>
                  <span style={{ color: '#1890ff', fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                    <LoadingOutlined style={{ fontSize: 14 }} />
                  </span>
                </div>
              </div>
            </div>
          ))}
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.4; }
            }
          `}</style>
        </>
      );
    }

    // 正常的 assistant 消息（有 content）
    return (
      <CustomAssistantMessage
        key={index}
        data-message-role="assistant"
        subComponent={message.generativeUI?.()}
        rawData={message}
        message={message}
        isLoading={inProgress && isCurrentMessage && !message.content}
        isGenerating={inProgress && isCurrentMessage && !!message.content}
        isCurrentMessage={isCurrentMessage}
        onRegenerate={() => onRegenerate?.(message.id)}
        onCopy={onCopy}
        onThumbsUp={onThumbsUp}
        onThumbsDown={onThumbsDown}
        markdownTagRenderers={markdownTagRenderers}
        ImageRenderer={ImageRenderer}
        toolExecutions={toolExecutions}
      />
    );
  }

  // Tool 消息：显示工具调用和结果（已合并）
  if (message.role === 'tool') {
    return (
      <ToolMessage
        key={index}
        message={message}
        isCurrentMessage={isCurrentMessage}
        inProgress={inProgress}
      />
    );
  }

  return null;
}
